// One place that turns import codes into words (Module 02 §4, §2.5).
//
// Two rules meet here:
//  * no hardcoded user-facing string — every label comes from the dictionary;
//  * entity NOUNS come from `useTerm()`, so the tenant's own vocabulary wins.
//    Copy in the dictionary therefore contains `{{product}}` / `{{workCenter}}`
//    placeholders which these helpers fill from the terminology feed.
//
// Every lookup carries a `defaultValue`, so a code the dictionary has not met
// yet degrades to the code itself rather than rendering `onboarding.…` at a
// factory admin.

import { enumLabel } from "../../../../lib/enum-label";
import { useTranslation } from "react-i18next";
import { useTerm } from "../../hooks/terminology";
import {
  kindMessageKey,
  rowMessageKey,
  type ImportJobStatus,
  type ImportKind,
  type ImportRowMessage,
  type ImportRowStatus,
} from "./import-model";

export interface ImportLabels {
  /** Terminology-resolved nouns, ready to spread into any `t()` call. */
  terms: Record<string, string>;
  kindLabel: (kind: ImportKind) => string;
  jobStatusLabel: (status: ImportJobStatus) => string;
  rowStatusLabel: (status: ImportRowStatus) => string;
  fieldLabel: (field: string) => string;
  /** One row message, resolved code → the lane's sentence → the shared
   * sentence → server message_key → code. */
  messageText: (message: ImportRowMessage, kind?: ImportKind) => string;
  severityLabel: (severity: ImportRowMessage["severity"]) => string;
  policyLabel: (policy: string) => string;
}

export function useImportLabels(): ImportLabels {
  const { t } = useTranslation();
  const { term } = useTerm();

  const terms: Record<string, string> = {
    product: term("product"),
    material: term("material"),
    bom: term("bom"),
    routingStep: term("routing_step"),
    workCenter: term("work_center"),
    facility: term("facility"),
    customer: term("customer"),
    supplier: term("supplier"),
    machine: term("machine"),
    openingStock: term("opening_stock"),
  };

  return {
    terms,
    kindLabel: (kind) =>
      t(`onboarding.importShared.kinds.${kind}`, { defaultValue: kind, ...terms }),
    jobStatusLabel: (status) => enumLabel(t, "onboarding.importShared.jobStatus", status),
    rowStatusLabel: (status) => enumLabel(t, "onboarding.importShared.rowStatus", status),
    fieldLabel: (field) =>
      t(`onboarding.importShared.fields.${field}`, { defaultValue: field, ...terms }),
    messageText: (message, kind) => {
      // The API ships a message_key, never translated prose (D-022); prefer our
      // own catalog, fall back to the server's key, then to the bare code.
      const params = messageParams(message);
      const shared = t(rowMessageKey(message.code), {
        defaultValue: t(message.message_key, { defaultValue: message.code, ...params, ...terms }),
        ...params,
        ...terms,
      });
      const own = kind === undefined ? undefined : kindMessageKey(kind, message.code);
      if (own === undefined) return shared;
      // A lane sentence with a `_named` form uses it when the row carries params,
      // and i18next falls back to the plain form when it has none.
      const named = Object.keys(params).length > 0 ? { context: "named" } : {};
      return t(own, { defaultValue: shared, ...params, ...terms, ...named });
    },
    severityLabel: (severity) => enumLabel(t, "onboarding.importShared.severity", severity),
    policyLabel: (policy) => enumLabel(t, "onboarding.importShared.duplicatePolicy", policy),
  };
}

/** i18next reads these option names itself, so a row param by one of them is not passed on. */
const RESERVED_OPTIONS = new Set(["count", "context", "defaultValue", "lng", "lngs", "ns"]);

/** A row message's text and number params, ready to interpolate. */
export function messageParams(message: ImportRowMessage): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(message.params ?? {})) {
    if (RESERVED_OPTIONS.has(key)) continue;
    if (typeof value === "string" || typeof value === "number") out[key] = value;
  }
  return out;
}
